import { BaseModel } from "~/model"
import { usePersonnelStore, useDataCacheStore } from "~/stores"
import { DateModel } from "~/model"
import { DriveFilesResource, InterventionResource, ProjetResource } from "~/api"
import { Api } from "~/api/Api"

export class ProjetModel extends BaseModel<ProjetResource> {
    static primaryKey = "IDPROJET"

    get etatProjet() {
        const id = this.IDETAT_PROJET
        switch (id) {
            case 1:
                return {
                    title: "Nouveau projet",
                    description:
                        "Nous étudions votre projet, merci de nous avoir consulté",
                    color: "green",
                    style: "background-color: rgb(44, 68, 18)",
                }
            case 2:
                return {
                    title: "Devis prêt",
                    description: "Votre devis est prêt et vous attend",
                    color: "green",
                    style: "background-color: rgb(44, 68, 18)",
                }
            case 3:
                return {
                    title: "Devis accepté",
                    description: "Votre devis est accepté",
                    color: "green",
                    style: "background-color: rgb(44, 68, 18)",
                }
            case 4:
                return {
                    title: "Commande en cours",
                    description: "Votre commande est en cours de réalisation",
                    color: "orange",
                    style: "background-color: rgb(214, 101, 21)",
                }
            case 5:
                return {
                    title: "Projet finalisé",
                    description: "Votre projet est finalisé",
                    color: "purple",
                    style: "background-color: rgb(130, 31, 223)",
                }
        }
    }

    // Montant TTC negocie du projet, null si ses devis ne sont pas charges ou s'il est
    // annule. Devis ni variantes ni refuses : tous tant que le projet n'est pas signe
    // (vert), seulement les devis signes ensuite (orange, violet).
    get montant(): number | null {
        if (this.REFUSE || !Array.isArray(this.DEVIS)) return null
        const signe = this.IDETAT_PROJET === 4 || this.IDETAT_PROJET === 5
        const devis = this.DEVIS.filter(
            (d) => !d.VARIANT && !d.REFUSE && (!signe || d.DEVIS_SIGNE),
        )
        if (!devis.length) return null
        return devis.reduce(
            (total, d) => total + (Number(d.MONTANT_NEGOCIE_TTC) || 0),
            0,
        )
    }

    get montantFormate(): string | null {
        const montant = this.montant
        if (montant === null) return null
        return new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
        }).format(montant)
    }

    // RDV non supprimes du projet, du plus recent au plus ancien ([] s'ils ne sont pas charges)
    get rdvsTries() {
        if (!Array.isArray(this.RDVS)) return []
        return this.RDVS.filter((rdv) => !rdv.SUPPRIME && rdv.RDV_DEBUT).sort(
            (a, b) => String(b.RDV_DEBUT).localeCompare(String(a.RDV_DEBUT)),
        )
    }

    // Prochain RDV a venir, a defaut le dernier passe
    get rdvRepere() {
        const rdvs = this.rdvsTries
        if (!rdvs.length) return null
        const now = new Date().toISOString()
        const aVenir = rdvs.filter((rdv) => String(rdv.RDV_DEBUT) >= now)
        return aVenir.length
            ? { rdv: aVenir[aVenir.length - 1], aVenir: true }
            : { rdv: rdvs[0], aVenir: false }
    }

    // Creneaux planifies des interventions (chantier), du plus recent au plus ancien
    get planningChantier() {
        if (!Array.isArray(this.INTERVENTIONS)) return []
        return this.INTERVENTIONS.flatMap((intervention) =>
            (intervention.INTERVENTION_PLANNING ?? []).map((creneau) => ({
                ...creneau,
                LIBELLE: intervention.LIBELLE,
                FINALISEE: intervention.FINALISEE,
            })),
        )
            .filter((creneau) => creneau.RDV_DEBUT)
            .sort((a, b) =>
                String(b.RDV_DEBUT).localeCompare(String(a.RDV_DEBUT)),
            )
    }

    // Reste a faire : null si la relation n'est pas chargee, sinon le detail
    get resteAFaire() {
        if (!this.RAF || !this.RAF.NB_LIGNES) return null
        return this.RAF
    }

    // Lignes du reste a faire en une chaine : "2 Fenêtres, 1 Volets roulants (cuisine)".
    // Le libelle du type de produit vient du datacache, charge au demarrage.
    get resteAFaireDetail() {
        const lignes = this.RAF?.LIGNES
        if (!Array.isArray(lignes) || !lignes.length) return ""
        const types = useDataCacheStore().data?.TYPE_PRODUIT_GENERIQUE ?? []
        return lignes
            .map((ligne) => {
                const type = types.find(
                    (item) =>
                        Number(item.IDTYPE_PRODUIT_GENERIQUE) ===
                        Number(ligne.IDTYPE_PRODUIT_GENERIQUE),
                )
                const libelle = [ligne.QTE_RAF || null, type?.LIBELLE]
                    .filter(Boolean)
                    .join(" ")
                return ligne.COMPLEMENT_RAF
                    ? `${libelle} (${ligne.COMPLEMENT_RAF})`.trim()
                    : libelle
            })
            .filter(Boolean)
            .join(", ")
    }

    get responsableProjet() {
        const personnelStore = usePersonnelStore()
        return personnelStore.itemById[this.IDRESPONSABLEPROJET]
    }

    get dateCreation() {
        if (!this.DATE_CREATION) return null
        return DateModel.formatDate(this.DATE_CREATION)
    }

    static async getPvInDriveFromIdProjet(
        id_projet: number,
    ): Promise<DriveFilesResource> {
        const params = {
            project_id: id_projet,
            origin: "intervention_sign_pv",
        }
        const getFilesReponse = await Api.drive.driveSearchFiles(params)
        const pdf = getFilesReponse?.data[0]

        if (!pdf?.url) throw new Error("Le PV n'existe pas")

        if (!pdf?.file_name.endsWith(".pdf"))
            throw new Error("Le fichier n'est pas un PDF")

        return pdf
    }
}

export interface ProjetModel extends ProjetResource {}
